import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@lf/analytics-events';
import { LoadingButton } from '@lf/ui-primitives';
import {
    submitCoppaAgeCheck,
    getCoppaConsentStatus,
} from '../api/lfApiChildLink';
import { readPendingShareToken } from '../lib/share/pendingShare';
import { useProfile } from '../contexts/ProfileContext';

function ChildAgeCheckPage() {
    const { fetchProfile } = useProfile();
    const [step, setStep] = useState('ask'); // 'ask' | 'parent_email' | 'pending'
    const [parentEmail, setParentEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const navigate = useNavigate();
    const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
    const isParentEmailValid = emailRegex.test(parentEmail.trim());

    // Fire `signup_started` on each step transition so the funnel
    // reports show drop-off between age_gate → consent → child_setup.
    // The `feature_blocked` event also fires when the under-13 path
    // gates the user behind COPPA parent consent.
    useEffect(() => {
        if (step === 'ask') {
            track('signup_started', { step: 'age_gate', app_role: 'child' });
        } else if (step === 'parent_email') {
            track('signup_started', { step: 'consent', app_role: 'child' });
            track('feature_blocked', { feature: 'app_access', reason: 'coppa_pending' });
        }
    }, [step]);

    async function handleAnswerNo() {
        setError('');
        setBusy(true);
        const resp = await submitCoppaAgeCheck(false);
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            if (fetchProfile) {
                await fetchProfile();
            }
            navigate('/home');
        } else {
            setError('Could not save your answer. Please try again.');
        }
    }

    function handleAnswerYes() {
        setStep('parent_email');
    }

    async function handleSubmitParentEmail(e) {
        if (e) e.preventDefault();
        setError('');
        if (!isParentEmailValid) {
            setError('Please enter a valid email address.');
            return;
        }
        setBusy(true);
        const resp = await submitCoppaAgeCheck(true, parentEmail.trim());
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            setStep('pending');
        } else {
            setError('Could not send the approval email. Please try again.');
        }
    }

    async function handleParentApproved() {
        setError('');
        setInfo('');
        setBusy(true);
        const resp = await getCoppaConsentStatus();
        setBusy(false);
        if (resp && resp.data && resp.data.status === 'granted') {
            if (fetchProfile) {
                await fetchProfile();
            }
            const shareToken = readPendingShareToken();
            if (shareToken) {
                navigate(`/share/${shareToken}`);
            } else {
                navigate('/home');
            }
        } else {
            setInfo('Not yet — your grown-up still needs to click the link.');
        }
    }

    if (step === 'pending') {
        return (
            <div className="mx-auto max-w-screen-md my-8">
                <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 text-left">
                    <h2 className="text-xl font-semibold mb-3">
                        Waiting for your grown-up
                    </h2>
                    <p className="text-sm text-gray-700 mb-2">
                        We sent an email to <strong>{parentEmail}</strong> with a
                        link they can click to say it&apos;s OK for you to use
                        Learnforest.
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                        Once they&apos;ve clicked the link, tap the button below.
                    </p>

                    {info && <div className="text-blue-600 text-sm mb-2">{info}</div>}
                    {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        text="My parent approved"
                        isLoading={busy}
                        onClick={handleParentApproved}
                    />
                </div>
            </div>
        );
    }

    if (step === 'parent_email') {
        return (
            <div className="mx-auto max-w-screen-md my-8">
                <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 text-left">
                    <h2 className="text-xl font-semibold mb-3">
                        We need a grown-up to say it&apos;s OK
                    </h2>
                    <p className="text-sm text-gray-700 mb-3">
                        Because you said you are under 13, a parent or guardian
                        needs to give us permission before you can use Learnforest.
                        Type their email below and we&apos;ll send them a link to
                        approve your account.
                    </p>
                    <form onSubmit={handleSubmitParentEmail} className="flex flex-col gap-2">
                        <input
                            type="email"
                            name="parent_email"
                            className="border border-gray-300 p-2 w-full"
                            placeholder="Parent's email address"
                            value={parentEmail}
                            onChange={e => setParentEmail(e.target.value)}
                        />
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <LoadingButton
                            type="submit"
                            className="bg-blue-500 text-white py-2 px-4 rounded self-start"
                            text="Send approval email"
                            isLoading={busy}
                            disabled={!isParentEmailValid}
                            onClick={handleSubmitParentEmail}
                        />
                    </form>
                </div>
            </div>
        );
    }

    // step === 'ask'
    return (
        <div className="mx-auto max-w-screen-md my-8">
            <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 text-left">
                <h2 className="text-xl font-semibold mb-3">
                    One quick question
                </h2>
                <p className="text-sm text-gray-700 mb-4">
                    Are you under 13 years old?
                </p>
                {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
                <div className="flex gap-2">
                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-6 rounded"
                        text="Yes"
                        isLoading={busy}
                        onClick={handleAnswerYes}
                    />
                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-6 rounded"
                        text="No"
                        isLoading={busy}
                        onClick={handleAnswerNo}
                    />
                </div>
            </div>
        </div>
    );
}

export default ChildAgeCheckPage;
